import TagManager from '@sooro-io/react-gtm-module'
import type { User } from 'fitify-types/src/types/user'
import { deepMerge } from 'fitify-utils/src/properties'
import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

type ContextProps = {
  pushUserData: (data: UserDataType) => void
}

const Context = createContext<ContextProps>({
  // eslint-disable-next-line no-empty-function
  pushUserData: () => {},
})

type UserDataType = {
  address?: {
    first_name?: string
    city?: string
    region?: string
    country?: string
    zip?: string
  }
  email_address?: string
  external_id?: string
  fb_login_id?: string
  gender?: User.Profile.Gender
}

export const EventContextProvider = ({ children }: PropsWithChildren) => {
  const [userData, setUserData] = useState<UserDataType | undefined>(undefined)

  const pushUserData = useCallback((data: UserDataType) => {
    setUserData((prevUserData) => deepMerge(prevUserData, data))
  }, [])

  useEffect(() => {
    if (userData) {
      TagManager.dataLayer({
        dataLayer: {
          user_data: userData,
        },
      })
    }
  }, [userData])

  const value = useMemo(() => {
    return {
      pushUserData,
    }
  }, [pushUserData])

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export const useEventContext = () => {
  const context = useContext(Context)
  if (!context) {
    throw new Error(
      'useEventContext must be used within a EventContextProvider'
    )
  }
  return context
}
